import {
  DomainError,
  EventFrequency,
  EventKind,
  FireScenario,
  getPensionDefaults,
  type ScenarioEventInit,
  type ScenarioSerializer,
} from '@fire-calc/domain-ts';

const SCHEMA_VERSION = 4;
const VALID_PHASES = new Set(['mvp', 'area2', 'area3', 'timing']);
const VALID_TIMINGS = new Set(['beginningOfYear', 'twicePerYear', 'endOfYear']);
const VALID_KINDS = new Set(['income', 'extraExpense']);
const VALID_FREQUENCIES = new Set(['monthly', 'yearly']);
const VALID_PENSION_TYPES = new Set(['standard', 'early', 'late', 'bridge', 'none']);

export class JsonScenarioSerializer implements ScenarioSerializer {
  toJson(scenario: FireScenario): unknown {
    return {
      schemaVersion: SCHEMA_VERSION,
      id: scenario.id,
      name: scenario.name,
      phase: scenario.phase,
      monthlyExpenses: scenario.monthlyExpenses.value,
      currentPortfolio: scenario.currentPortfolio.value,
      annualReturn: scenario.annualReturn.value,
      inflation: scenario.inflation.value,
      currentAge: scenario.currentAge.value,
      retirementAge: scenario.retirementAge.value,
      monthlyContribution: scenario.monthlyContribution.value,
      withdrawalTiming: scenario.withdrawalTiming,
      swr: scenario.swr.value,
      contributionGrowthEnabled: scenario.contributionGrowthEnabled,
      events: scenario.events.map((e) => ({
        id: e.id,
        kind: e.kind,
        label: e.label,
        amount: e.amount.value,
        startAge: e.startAge.value,
        frequency: e.frequency,
        durationYears: e.durationYears,
        inflationAdjusted: e.inflationAdjusted,
        enabled: e.enabled,
        note: e.note ?? null,
        pensionType: e.pensionType ?? null,
        growthRate: e.growthRate ?? null,
      })),
      createdAt: scenario.createdAt.toISOString(),
      updatedAt: scenario.updatedAt.toISOString(),
    };
  }

  fromJson(json: unknown): FireScenario {
    if (typeof json !== 'object' || json === null) {
      throw DomainError.serialization('Scenario JSON must be an object');
    }
    const raw = json as Record<string, unknown>;

    if (raw.schemaVersion !== SCHEMA_VERSION) {
      throw DomainError.serialization(`Unsupported schema version: ${String(raw.schemaVersion)}`);
    }
    requireString(raw, 'id');
    requireString(raw, 'name');
    requireNumberish(raw, 'monthlyExpenses');
    requireInteger(raw, 'currentAge');
    requireInteger(raw, 'retirementAge');

    if (raw.withdrawalTiming !== undefined) {
      if (
        typeof raw.withdrawalTiming !== 'string' ||
        !VALID_TIMINGS.has(raw.withdrawalTiming)
      ) {
        throw DomainError.serialization(
          `Type mismatch on field: withdrawalTiming (${String(raw.withdrawalTiming)})`,
        );
      }
    }
    if (raw.phase !== undefined) {
      if (typeof raw.phase !== 'string' || !VALID_PHASES.has(raw.phase)) {
        throw DomainError.serialization(
          `Type mismatch on field: phase (${String(raw.phase)})`,
        );
      }
    }

    const events = Array.isArray(raw.events)
      ? raw.events.map((item) => validateEvent(item))
      : [];

    return FireScenario.create({
      id: String(raw.id),
      name: String(raw.name),
      monthlyExpenses: toNumber(raw.monthlyExpenses),
      currentPortfolio:
        raw.currentPortfolio !== undefined ? toNumber(raw.currentPortfolio) : 0,
      annualReturn:
        raw.annualReturn !== undefined ? toNumber(raw.annualReturn) : 0.07,
      inflation: raw.inflation !== undefined ? toNumber(raw.inflation) : 0.03,
      currentAge: Number(raw.currentAge),
      retirementAge: Number(raw.retirementAge),
      monthlyContribution:
        raw.monthlyContribution !== undefined
          ? toNumber(raw.monthlyContribution)
          : 0,
      withdrawalTiming:
        (raw.withdrawalTiming as 'beginningOfYear' | 'twicePerYear' | 'endOfYear' | undefined) ??
        'beginningOfYear',
      swr: raw.swr !== undefined ? toNumber(raw.swr) : 0.04,
      contributionGrowthEnabled:
        typeof raw.contributionGrowthEnabled === 'boolean'
          ? raw.contributionGrowthEnabled
          : true,
      events,
      phase:
        (raw.phase as 'mvp' | 'area2' | 'area3' | 'timing' | undefined) ?? 'mvp',
      createdAt: new Date(String(raw.createdAt)),
      updatedAt: new Date(String(raw.updatedAt)),
    });
  }
}

function validateEvent(item: unknown): ScenarioEventInit {
  if (typeof item !== 'object' || item === null) {
      throw DomainError.serialization('Event item must be an object');
    }
  const raw = item as Record<string, unknown>;
  requireString(raw, 'id');
  requireString(raw, 'label');
  requireNumberish(raw, 'amount');
  requireInteger(raw, 'startAge');
  if (typeof raw.kind !== 'string' || !VALID_KINDS.has(raw.kind)) {
      throw DomainError.serialization(`Type mismatch on event field: kind (${String(raw.kind)})`);
    }
  if (raw.frequency !== undefined) {
    if (
      typeof raw.frequency !== 'string' ||
      !VALID_FREQUENCIES.has(raw.frequency)
    ) {
      throw DomainError.serialization(
        `Type mismatch on event field: frequency (${String(raw.frequency)})`,
      );
    }
  }

  // pensionType: optional — v3 files won't have it (defaults to undefined)
  let pensionType: 'standard' | 'early' | 'late' | 'bridge' | 'none' | undefined;
  if (raw.pensionType !== undefined && raw.pensionType !== null) {
    if (
      typeof raw.pensionType !== 'string' ||
      !VALID_PENSION_TYPES.has(raw.pensionType)
    ) {
      throw DomainError.serialization(
        `Type mismatch on event field: pensionType (${String(raw.pensionType)})`,
      );
    }
    pensionType = raw.pensionType as 'standard' | 'early' | 'late' | 'bridge' | 'none';
  }

  // growthRate: optional — defaults to per-type default when pensionType is set
  let growthRate: number | undefined;
  if (raw.growthRate !== undefined && raw.growthRate !== null) {
    if (typeof raw.growthRate !== 'number') {
      throw DomainError.serialization(
        `Type mismatch on event field: growthRate (${String(raw.growthRate)})`,
      );
    }
    growthRate = raw.growthRate;
  } else if (pensionType != null) {
    // Migration: v3 files missing growthRate get per-type default
    growthRate = getPensionDefaults(pensionType).growthRate;
  }

  const eventInit: ScenarioEventInit = {
    id: String(raw.id),
    kind: raw.kind as EventKind,
    label: String(raw.label),
    amount: toNumber(raw.amount),
    startAge: Number(raw.startAge),
    frequency:
      (raw.frequency as EventFrequency | undefined) ?? EventFrequency.monthly,
    durationYears: typeof raw.durationYears === 'number' ? raw.durationYears : 0,
    inflationAdjusted:
      typeof raw.inflationAdjusted === 'boolean'
        ? raw.inflationAdjusted
        : true,
    enabled: typeof raw.enabled === 'boolean' ? raw.enabled : true,
  };
  if (typeof raw.note === 'string') eventInit.note = raw.note;
  if (pensionType != null) eventInit.pensionType = pensionType;
  if (growthRate != null) eventInit.growthRate = growthRate;
  return eventInit;
}

function requireString(raw: Record<string, unknown>, key: string): void {
  if (typeof raw[key] !== 'string' || (raw[key] as string).length === 0) {
    throw DomainError.serialization(`Missing required string field: ${key}`);
  }
}

function requireInteger(raw: Record<string, unknown>, key: string): void {
  if (typeof raw[key] !== 'number' || !Number.isInteger(raw[key] as number)) {
    throw DomainError.serialization(`Type mismatch on field: ${key}`);
  }
}

function requireNumberish(raw: Record<string, unknown>, key: string): void {
  const v = raw[key];
  if (typeof v === 'number') return;
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) return;
  throw DomainError.serialization(`Missing required numeric field: ${key}`);
}

function toNumber(v: unknown): number {
  return typeof v === 'string' ? Number(v) : (v as number);
}
